// Backend-Log-Buffer fuer /system/logs. Server haelt die letzten 500
// Zeilen im RAM (kein Persist, kein Disk-Tail) -- bei Service-Restart
// faengt's bei null an. SWR-Cache faengt nur die Browser-Reload-
// Sekunden ab, nicht einen Service-Neustart.
//
// Live-Pfad: WS-Channel "logs" published jede neue Zeile sofort. Der
// 30s-Poll bleibt als Reconnect-Failsafe, damit ein verlorener WS-Tick
// nicht heimlich Loecher in die Liste reisst.
//
// Beides -- Poll UND WS-Abo -- läuft nur, solange jemand die Logs-Seite
// ansieht (beobachteLogs, lib/bei-bedarf.ts): sonst kostete der Store auf
// jeder Seite 58 kB/min plus jede Zeile über die WS (Messung 03.11.2026).
// start* spielt nur den SWR-Cache ein.

import { untrack } from "svelte";
import { SvelteMap } from "svelte/reactivity";
import { beiBedarf } from "./bei-bedarf";
import { registerRevalidator } from "./revalidate.svelte";

import { loadPersisted, persist } from "./persistent-summary.svelte";
import type { LogEntry } from "./types";
import { subscribe as wsSubscribe } from "./ws-hub.svelte";

const STORAGE_KEY = "logs";
// Backend-Buffer-Cap (logbuf.NewBuffer(500) in cmd/homelab-dash/main.go).
// Frontend trimmt auf denselben Wert, damit Live-Appends nicht ewig
// wachsen.
const MAX_LINES = 500;

const _state = new SvelteMap<string, LogEntry[]>();
let _started = false;
// Bei Log-Bursts (z.B. Reboot-Marathon, 50-100 Eintraege/Sek) wird
// jedes Live-Append sonst zur Heap-Falle:
//   * cur.slice(...).concat(entry) allokiert ein 500-Eintraege-Array
//   * _state.set(...) triggert komplette Svelte-Reactivity
//   * persist() macht JSON.stringify(500) + localStorage.setItem
// Pro Sekunde gehen so MBs an temp-Strings durch die JS-Heap; Safari
// schmeisst die Seite irgendwann mit "significant memory" raus.
// Batching: Append-Events sammeln wir in _pending, flush einmal pro
// FLUSH_MS (alle Live-Konsumenten sehen dann eine fast-live Update-
// Rate). Persist lauft mit eigenem, laengeren Throttle hinterher.
const FLUSH_MS = 250;
const PERSIST_MS = 2_000;
let _pending: LogEntry[] = [];
let _flushTimer: ReturnType<typeof setTimeout> | undefined;
let _persistTimer: ReturnType<typeof setTimeout> | undefined;

function flushPending(): void {
  _flushTimer = undefined;
  if (_pending.length === 0) return;
  const cur = _state.get("current") ?? [];
  const merged = cur.concat(_pending);
  _pending = [];
  const next = merged.length > MAX_LINES
    ? merged.slice(merged.length - MAX_LINES)
    : merged;
  _state.set("current", next);
  schedulePersist();
}

function schedulePersist(): void {
  if (_persistTimer) return;
  _persistTimer = setTimeout(() => {
    _persistTimer = undefined;
    const cur = _state.get("current");
    if (cur) persist(STORAGE_KEY, cur);
  }, PERSIST_MS);
}

async function refresh(): Promise<void> {
  try {
    const r = await fetch("/v2-api/logs", { credentials: "include" });
    if (!r.ok) return;
    const list = (await r.json()) as LogEntry[];
    _state.set("current", list);
    persist(STORAGE_KEY, list);
  } catch {
    /* ignore */
  }
}

function appendLive(entry: LogEntry): void {
  _pending.push(entry);
  // Wenn der Pending-Puffer schon ueber dem Cap ist, gibt's nichts mehr
  // zu sammeln -- die alten Zeilen wuerden eh wegfallen.
  if (_pending.length > MAX_LINES) {
    _pending = _pending.slice(_pending.length - MAX_LINES);
  }
  if (!_flushTimer) _flushTimer = setTimeout(flushPending, FLUSH_MS);
}

// Poll-Intervall 30s (nicht 5s) -- WS deckt den Live-Pfad ab, Polling ist
// nur Reconnect-Failsafe.
const _bedarf = beiBedarf({
  hole: refresh,
  takt: 30_000,
  abo: () =>
    wsSubscribe("logs", (data) => {
      const entry = data as LogEntry | undefined;
      if (!entry || typeof entry.ts !== "number") return;
      appendLive(entry);
    }),
});

// Nur den SWR-Cache einspielen und den Revalidator eintragen; geholt wird
// erst mit einem Beobachter.
export function startLogsSummary(): void {
  if (_started) return;
  _started = true;
  registerRevalidator(() => {
    if (_bedarf.aktiv()) void refresh();
  });
  const cached = loadPersisted<LogEntry[]>(STORAGE_KEY);
  if (cached) _state.set("current", cached);
}

// Anmelden, solange eine Ansicht die Logs zeigt; der Rückgabewert meldet
// wieder ab. In Svelte: $effect(() => { if (zuSehen()) return beobachteLogs(); }).
export function beobachteLogs(): () => void {
  return untrack(() => {
    startLogsSummary();
    return _bedarf.beobachte();
  });
}

export function stopLogsSummary(): void {
  _started = false;
  _bedarf.beende();
  if (_flushTimer) {
    clearTimeout(_flushTimer);
    _flushTimer = undefined;
  }
  if (_persistTimer) {
    clearTimeout(_persistTimer);
    _persistTimer = undefined;
  }
  _pending = [];
  _state.clear();
}

export function getLogs(): LogEntry[] | undefined {
  return _state.get("current");
}
